// One client in the netcode harness: the REAL client netcode (createRemoteGame
// mirror + createOwnWormController prediction), wired to the in-process bus, with
// the same clock model the live client uses (own-worm predicted at a local clock,
// rtt-measured one-way latency, segments streamed every 33ms). A client can carry
// SEVERAL local players (like the real client's local-multiplayer): each gets its
// own predictor + steering source; a local player with a `drive` fn is bot-steered
// (imitating a second human on the same machine), one without takes the keyboard
// via setSteering. Exposes the state the debug page renders (smooth via
// getRenderTime, truth via getServerTime, plus the predictors + server-truth segments).
import { createRemoteGame } from "../../../online/remote-game";
import { createOwnWormController } from "../../../online/prediction";
import type { HarnessBus } from "./harness-bus";

function clamp(v: number, lo: number, hi: number) { return v < lo ? lo : v > hi ? hi : v; }

export type DriveFn = (gs: any, tSec: number, myId: string | null) => number;

export interface HarnessLocalPlayer {
  name: string;
  colorId: string;
  drive?: DriveFn; // bot steering for this local player; omit for the keyboard player
}

export interface HarnessClient {
  id: string;
  myId(): string | null; // the PRIMARY local player (first seat)
  remote: ReturnType<typeof createRemoteGame>;
  predictors(): Record<string, any>;
  localIds(): string[];
  getGameState(): any;
  getRenderTime(): number; // delayed / smoothed render clock
  getServerTime(): number; // latest server clock (no render delay) - "truth" render clock
  ownNow(): number; // the own-worm prediction target (local clock)
  setSteering(s: number): void; // keyboard funnels here (the drive-less local player)
  // The primary local player's worm: has its LOCAL prediction detected a crash (client
  // truth), and the server game-time at which it did (for comparing death timing across views).
  ownCrashed(): boolean;
  ownCrashTime(): number | null;
  dispose(): void;
}

export function createHarnessClient(opts: {
  bus: HarnessBus;
  id: string;
  name?: string;
  colorId?: string;
  renderDelayMs?: number;
  drive?: DriveFn; // shorthand: a single bot-steered local player
  // Full multi-local form; overrides name/colorId/drive when given.
  localPlayers?: HarnessLocalPlayer[];
}): HarnessClient {
  const { bus, id } = opts;
  const specs: HarnessLocalPlayer[] = (opts.localPlayers && opts.localPlayers.length)
    ? opts.localPlayers
    : [{ name: opts.name || "Player", colorId: opts.colorId || "blue", drive: opts.drive }];
  const socket = bus.addClient(id);
  const remote = createRemoteGame((opts.renderDelayMs ?? 100) / 1000);
  // One seat per local player, filled from `assigned` (server keeps the order).
  const locals: { spec: HarnessLocalPlayer; pid: string; predictor: any; lastSteering: number }[] = [];
  const predictorsMap: Record<string, any> = {};
  let loop: any = null;
  let rttTimer: any = null;
  let currentSteering = 0; // keyboard, for the drive-less local player
  let lastSegSend = 0;
  let oneWay = 0; // seconds
  let crashReported = false; // reported the PRIMARY player's crash this round
  let ownCrashTime: number | null = null; // server game-time we locally detected it
  let roundActive = false; // false during the post-win victory lap (round already scored)
  // own-worm local clock (mirrors RemoteGame's ownNow)
  let t0 = 0;
  let wall0 = Date.now();

  function serverNow() { return remote.getServerTime() + oneWay; }
  function ownNow() {
    const est = t0 + (Date.now() - wall0) / 1000;
    // Re-anchor on a big drift (tab stall / desync), like the live client.
    if (Math.abs(est - serverNow()) > 0.3) { t0 = serverNow(); wall0 = Date.now(); return t0; }
    return est;
  }

  function stopLoop() { if (loop) { clearInterval(loop); loop = null; } }
  function startLoop() {
    stopLoop();
    locals.forEach((l) => { l.lastSteering = NaN; });
    loop = setInterval(() => {
      if (!locals.length) return;
      const gs = remote.getGameState();
      if (!gs) return;
      const now = Date.now();
      const sendTick = now - lastSegSend >= 33;
      for (const l of locals) {
        const s = l.spec.drive ? l.spec.drive(gs, (now - wall0) / 1000, l.pid) : currentSteering;
        if (s !== l.lastSteering) { socket.emit("player_steering", { playerId: l.pid, steering: s }); l.lastSteering = s; }
        l.predictor.setSteering(s);
        l.predictor.predict(gs, ownNow());
        // Register any twin we just spawned BEFORE streaming its segments (same connection is
        // in-order), so the host has the worm when its first segments arrive.
        const newWorms = l.predictor.drainNewWorms ? l.predictor.drainNewWorms() : [];
        for (const worm of newWorms) socket.emit("worm_spawn", { playerId: l.pid, worm });
        if (sendTick) {
          const segments = l.predictor.drainSegments(gs);
          if (segments.length) socket.emit("worm_segments", { playerId: l.pid, segments });
          // The client owns its worms and detects each one's crash locally; report each
          // by wormId once so the server kills the right worm (a twin gives two) and the
          // round can end (mirrors RemoteGame).
          const crashed = l.predictor.drainCrashes ? l.predictor.drainCrashes() : [];
          for (const wormId of crashed) {
            if (l === locals[0] && !crashReported) { crashReported = true; ownCrashTime = remote.getServerTime(); }
            // Only tell the server during a live round; a post-win victory-lap crash is
            // purely cosmetic. Mark the crash locally (visual) either way.
            if (roundActive) {
              const ci = l.predictor.crashInfo ? l.predictor.crashInfo(wormId) : null;
              socket.emit("worm_crashed", { playerId: l.pid, wormId, cause: ci?.cause, killerTrackId: ci?.killer, cell: ci?.cell?.index });
            }
            const dw = gs.worms.find((w: any) => w.id === wormId);
            if (dw) dw.alive = false;
          }
          if (crashed.length && l.predictor.isDead && l.predictor.isDead()) {
            const dp = gs.players && gs.players.find((pl: any) => pl.id === l.pid);
            if (dp) dp.alive = false;
          }
        }
      }
      if (sendTick) lastSegSend = now;
    }, 16);
  }

  function startRtt() {
    if (rttTimer) clearInterval(rttTimer);
    const ping = () => {
      const sentAt = Date.now();
      socket.timeout(2000).emit("rtt_ping", {}, (err: any) => {
        if (err) return;
        const rttSec = (Date.now() - sentAt) / 1000;
        const measured = clamp(rttSec / 2, 0, 0.25);
        oneWay = oneWay ? oneWay * 0.7 + measured * 0.3 : measured; // smooth
      });
    };
    ping();
    rttTimer = setInterval(ping, 2000);
  }

  socket.on("connect", () => {
    socket.emit("play", {
      name: specs[0].name,
      colorId: specs[0].colorId,
      localPlayers: specs.map((s) => ({ name: s.name, colorId: s.colorId })),
      wire: 3,
    });
    startRtt();
  });
  socket.on("assigned", (m: any) => {
    const ids: string[] = (m.playerIds && m.playerIds.length ? m.playerIds : [m.playerId]).filter(Boolean);
    locals.length = 0;
    ids.forEach((pid, i) => {
      const spec = specs[Math.min(i, specs.length - 1)];
      const predictor = createOwnWormController(pid, { disableJumps: false, getOpponentClipTime: () => remote.getRenderTime() });
      predictorsMap[pid] = predictor;
      locals.push({ spec, pid, predictor, lastSteering: NaN });
    });
    remote.setLocalPlayerIds(ids);
  });
  socket.on("game_start", (m: any) => {
    remote.setInitial(m.gameState);
    remote.setLocalPlayerIds(locals.map((l) => l.pid));
    locals.forEach((l) => l.predictor.reset(remote.getGameState()));
    crashReported = false;
    ownCrashTime = null;
    roundActive = true;
    t0 = remote.getServerTime();
    wall0 = Date.now();
    startLoop();
  });
  socket.on("game_update", (m: any) => remote.applyUpdate(m.data));
  // Switcharoonie (server-authoritative): hold our worms at the freeze time + swap owners, then
  // re-bind + resume at go. Every local predictor obeys against its own prediction clock.
  socket.on("switch_freeze", (m: any) => {
    locals.forEach((l) => l.predictor.switchFreeze(m.atTime, m.assignments));
    const gs = remote.getGameState();
    if (gs) gs.switchAnim = { freezeTime: m.atTime, goTime: null, assignments: m.assignments };
  });
  socket.on("switch_go", (m: any) => {
    locals.forEach((l) => l.predictor.switchGo(m.atTime));
    const gs = remote.getGameState();
    if (gs && gs.switchAnim) gs.switchAnim.goTime = m.atTime;
  });
  // The host rejected a twin we spawned (we lost a contested grab): drop it.
  socket.on("worm_rejected", (m: any) => {
    const gs = remote.getGameState();
    if (!gs) return;
    locals.forEach((l) => { if (l.predictor.removeWorm) l.predictor.removeWorm(gs, m.wormId); });
  });
  // Round over: keep the loop running so a surviving worm can drive a victory lap
  // (and crash on it, locally, without affecting the score). Fully stop at match end.
  socket.on("game_over", () => { roundActive = false; });
  socket.on("match_over", () => { roundActive = false; stopLoop(); });

  return {
    id,
    myId: () => (locals.length ? locals[0].pid : null),
    remote,
    predictors: () => predictorsMap,
    localIds: () => locals.map((l) => l.pid),
    getGameState: () => remote.getGameState(),
    getRenderTime: () => remote.getRenderTime(),
    getServerTime: () => remote.getServerTime(),
    ownNow,
    setSteering: (s: number) => { currentSteering = s; },
    ownCrashed: () => crashReported,
    ownCrashTime: () => ownCrashTime,
    dispose: () => { stopLoop(); if (rttTimer) clearInterval(rttTimer); try { socket.disconnect(); } catch { /* */ } },
  };
}
